import { apiGet } from './client';

export interface LabelCount {
  label: string;
  count: number;
}

export interface RailRevenue {
  activeSubscriptions: number;
  trials: number;
  mrrCents: number;
  priced: number;
  unpriced: number;
}

export interface MrrEstimate {
  currency: 'USD';
  mrrCents: number;
  arrCents: number;
  activeSubscriptions: number;
  trials: number;
  byRail: { stripe: RailRevenue; apple: RailRevenue };
  basis: 'estimated' | 'partial' | 'unavailable';
}

// As encoded on the document (ScanEvent.realIdCompliance). Optional — older
// API builds won't send it, so callers must treat its absence as "unknown",
// not zero.
export interface RealIdBreakdown {
  compliant: number;
  nonCompliant: number;
  notEncoded: number;
  notReported: number;
}

export interface PlatformOverview {
  windowDays: number;
  generatedAt: string;
  teams: {
    total: number;
    newInWindow: number;
    suspended: number;
    byTier: LabelCount[];
    byStatus: LabelCount[];
  };
  venues: { total: number; active: number; newInWindow: number };
  members: {
    total: number;
    pendingInvites: number;
    newInWindow: number;
    byRole: LabelCount[];
  };
  devices: {
    total: number;
    active: number;
    revoked: number;
    seenLast24h: number;
    newInWindow: number;
  };
  scans: {
    allTime: number;
    window: number;
    today: number;
    accepted: number;
    rejected: number;
    undetermined: number;
    rejectionRate: number;
    passbackAlerts: number;
    topRejectionReasons: LabelCount[];
    realId?: RealIdBreakdown;
  };
  subscriptions: {
    active: number;
    trials: number;
    appleActive: number;
    stripeActive: number;
    byProduct: LabelCount[];
    byStripeTier: LabelCount[];
  };
  revenue: MrrEstimate;
  topTeams: { id: string; name: string; scans: number }[];
  funnel: { signedUp: number; subscribed: number; activated: number };
  health: {
    staleSubscriptionStatuses: number;
    devicesSeenLast24h: number;
    errorsLast7d: number;
    lastConsumerAggregationAt: string | null;
  };
}

export type GrowthMetric =
  | 'teams'
  | 'subscriptions'
  | 'venues'
  | 'members'
  | 'scans';
export type GrowthInterval = 'day' | 'week' | 'month';

export interface GrowthSeries {
  metric: GrowthMetric;
  interval: GrowthInterval;
  start: string;
  end: string;
  totalNew: number;
  points: { period: string; new: number; cumulative: number }[];
}

export const getPlatformOverview = (windowDays = 30): Promise<PlatformOverview> =>
  apiGet<PlatformOverview>('/admin/platform/overview', { windowDays });

export const getPlatformGrowth = (
  metric: GrowthMetric,
  interval: GrowthInterval,
  rangeDays: number,
): Promise<GrowthSeries> =>
  apiGet<GrowthSeries>('/admin/platform/growth', {
    metric,
    interval,
    rangeDays,
  });

// ── MRR history (daily snapshot table; empty until the nightly cron has run) ─

export interface MrrHistoryPoint {
  date: string;
  mrr: number;
  arr: number;
  basis: string;
}

export interface MrrHistoryResult {
  points: MrrHistoryPoint[];
}

export const getMrrHistory = (days = 90): Promise<MrrHistoryResult> =>
  apiGet<MrrHistoryResult>('/admin/platform/mrr-history', { days });

// ── Conversion (trial→paid + free→Pro funnel) ────────────────────────────────

export type DataBasis = 'estimated' | 'partial' | 'ok' | 'unavailable';

export interface ConversionStats {
  trials: {
    started: number;
    converted: number;
    expired: number;
    rate: number | null;
    basis: DataBasis;
  };
  proFunnel: {
    paywallViews: number;
    purchases: number;
    rate: number | null;
  };
}

export const getConversion = (windowDays = 30): Promise<ConversionStats> =>
  apiGet<ConversionStats>('/admin/platform/conversion', { windowDays });

// ── Solo-Pro engagement (anonymous — no emails/ids in topUsers) ──────────────

export interface SoloTopUser {
  scanCount: number;
  lastActiveDate: string | null;
}

export interface SoloUsage {
  totalUsers: number;
  activeLast30d: number;
  lifetimeScans: number;
  topUsers: SoloTopUser[];
}

export const getSoloUsage = (): Promise<SoloUsage> =>
  apiGet<SoloUsage>('/admin/platform/solo-usage');
